import { Textarea } from '@ibsys/kit';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PhraseAuditItem, useUpdatePhraseText } from '@/entities/phrases';
import { usePhrasePlayback, usePhrasePlaybackStore } from '@/features/phrase-playback';
import { getPaddedPhrasePlaybackKey, getPhrasePlaybackKey } from '../model/phrasePlaybackKeys';

type Props = {
  /** Правимая реплика */
  phrase: PhraseAuditItem;
};

/**
 * Текст реплики в строке перечня — само поле правки: курсор ставится прямо в него, а уход
 * курсора сохраняет реплику, отмечая её пройденной вычиткой. Отдельных кнопок перехода
 * в правку, сохранения и отмены нет; пустое поле реплику не меняет.
 */
export const PhraseTextEditor: FC<Props> = ({ phrase }) => {
  const { t } = useTranslation(['audioAudit']);
  const [inputValue, setInputValue] = useState(phrase.text ?? '');
  const { mutateAsync, isPending } = useUpdatePhraseText();
  const { isPlaying, isLoading, isFollowEnabled, start } = usePhrasePlayback({
    conversationId: phrase.conversationId,
    startedAt: phrase.startedAt,
    finishedAt: phrase.finishedAt,
    playbackKey: getPhrasePlaybackKey(phrase.id),
    leadMs: 0,
  });

  /** Эта же реплика звучит с запасом по краям или её запись получают для такого прослушивания */
  const paddedKey = getPaddedPhrasePlaybackKey(phrase.id);
  const isPaddedActive = usePhrasePlaybackStore(
    (state) => state.playingKey === paddedKey || state.loadingKey === paddedKey,
  );

  /** Курсор в поле: пришедшее с сервера значение набранный текст не перебивает */
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) return;
    setInputValue(phrase.text ?? '');
  }, [phrase.text]);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  }, []);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    // Возврат курсора в то же поле реплику не перезапускает: она уже звучит или её запись получают —
    // в том числе когда её запустили с запасом по краям, а запас курсором не перебивается
    if (isFollowEnabled && !isPlaying && !isLoading && !isPaddedActive) void start({ isFollowing: true });
  }, [isFollowEnabled, isPlaying, isLoading, isPaddedActive, start]);

  const handleBlur = useCallback(async () => {
    isFocusedRef.current = false;

    const trimmed = inputValue.trim();
    // Опустошённое поле правкой не считается: реплика остаётся с прежним текстом и непройденной
    if (!trimmed) {
      setInputValue(phrase.text ?? '');
      return;
    }

    try {
      // Сохраняем и при неизменном тексте: пройденная вычиткой реплика — такая же
      // обновлённая, как исправленная
      await mutateAsync({ id: phrase.id, text: trimmed });
    } catch {
      // Ошибку показал глобальный перехватчик запросов; поле возвращаем к сохранённому значению
      setInputValue(phrase.text ?? '');
    }
  }, [phrase.id, phrase.text, inputValue, mutateAsync]);

  return (
    <Textarea
      className="flex-1"
      value={inputValue}
      disabled={isPending}
      aria-label={t('audioAudit:editText')}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};
